import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { QUERY_KEYS } from '../../constants';
import { useBetslipContext } from '../../contexts/BetslipContext';
import useLocalization from '../../hooks/useLocalization';
import { useSkinStyles } from '../../hooks/useSkinStyles';
import { useCountPendingBetslips } from '../../queries';
import { REOFFERED_BETSLIP_ACTIONS_SKIN_STYLES } from '../../styles/components/reofferedBetslipActions';
import { patchData } from '../../utils/api';
import { API_ROUTES } from '../../constants/apiRoutes';

type ReofferedBetslipActionsProps = {
  currentBetslip: string;
};

const ReofferedBetslipActions = ({ currentBetslip }: ReofferedBetslipActionsProps) => {
  const { t } = useLocalization();
  const { enqueueSnackbar } = useSnackbar();
  const { removeReofferedBettingSlip } = useBetslipContext();
  const queryClient = useQueryClient();
  const { refetch: refetchPendingBetslipsCount } = useCountPendingBetslips();
  const styles = useSkinStyles(REOFFERED_BETSLIP_ACTIONS_SKIN_STYLES);

  const acceptOffer = useMutation({
    mutationFn: (bettingSlipId: string) => patchData(`${API_ROUTES.MY_BETS}/${bettingSlipId}`, { accept: true }),
    onSuccess: (_data, bettingSlipId) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.betslip, bettingSlipId],
        exact: true,
        refetchType: 'none',
      });
      enqueueSnackbar(t('confirmed'), { variant: 'success' });
      removeReofferedBettingSlip(bettingSlipId);
      refetchPendingBetslipsCount();
    },
  });

  const rejectOffer = useMutation({
    mutationFn: (bettingSlipId: string) => patchData(`${API_ROUTES.MY_BETS}/${bettingSlipId}`, { accept: false }),
    onSuccess: (_data, bettingSlipId) => {
      enqueueSnackbar(t('bettingSlipRejected'), { variant: 'error' });
      removeReofferedBettingSlip(bettingSlipId);
      refetchPendingBetslipsCount();
    },
  });

  return (
    <Box sx={styles.root}>
      <Button
        onClick={() => acceptOffer.mutate(currentBetslip)}
        variant='contained'
        sx={styles.acceptBtn}
        disabled={!currentBetslip || acceptOffer.isPending}
      >
        {t('accept')}
      </Button>
      <Button
        onClick={() => rejectOffer.mutate(currentBetslip)}
        variant='contained'
        sx={styles.rejectBtn}
        disabled={!currentBetslip || rejectOffer.isPending}
      >
        {t('reject')}
      </Button>
    </Box>
  );
};

export default ReofferedBetslipActions;
